import { API } from "@gui/infrastructure/pass-through/api";
import { GEO_VALIDATOR_SOURCE, STATUS_CALCULATOR_SOURCE, type IssueQueue, type Job } from "@domain/CRM/job";
import type { JobAgeBasis } from "@domain/CRM/job";
import type { JobDetailTabActionKind } from "@gui/widgets/JobDetail/JobDetailTabAction";

export type JobErrorTabConfig = {
	label: string;
	/** The error source the job cards list on this tab; null lists every error with its source. */
	errorSource: string | null;
	/** The date the job cards' age chip counts from — the one this tab's query windows on. */
	ageFrom: JobAgeBasis;
	/** The job cards also show the job's Data Sent flags and whether its CSV was sent to the lab. */
	showDataSent?: boolean;
	/** Shown (and fetched) only for developers. */
	developerOnly?: boolean;
	/** The issues queue this tab lists: the one `fetch` reads, and the one the page's count badge counts. */
	queue: IssueQueue;
	fetch: () => Promise<Job[]>;
	/** A job action this tab offers in the job detail's toolbar. Its request resolves once the work is done, after
	 *  which the page refetches (the job usually leaves the tab). */
	action?: {
		kind: JobDetailTabActionKind;
		failure: string;
		/** The ways to run it; several make the toolbar button a menu. */
		choices: JobErrorTabActionChoice[];
	};
	/** An action offered only for a multi-job selection, where the tab has no single-job `action` of its own. */
	batchOnlyAction?: JobErrorBatchAction;
};

/** One way to run a tab's action on a job. */
export type JobErrorTabActionChoice = {
	label: string;
	perform: (jobId: string) => Promise<unknown>;
	/** What a failure of this choice is called, where the action's own `failure` doesn't describe it. */
	failure?: string;
	/** Set when the request only queues the work instead of waiting for it: how long the page then waits before it
	 *  refetches, so the job has had time to leave the tab. */
	settleMs?: number;
};

/** What a batch over the selected jobs can run: the tab's action minus its toolbar presentation. */
export type JobErrorBatchAction = { failure: string; choices: JobErrorTabActionChoice[] };

const EXIT_INTERVIEW_SOURCE = "Exit Interview Lambda";

/** The resend only queues the emailer and answers at once, so the page gives it this long before it refetches. */
const SENDBACK_SETTLE_MS = 30_000;

/** The subsets of job errors the page can show, each with its own endpoint (the Exe tab took over from the retired
 *  Exe Mission Validation page). */
export const JOB_ERROR_TABS = {
	geo: {
		label: "Geo Validation",
		errorSource: GEO_VALIDATOR_SOURCE,
		ageFrom: "dateCreated",
		queue: "geo-validation",
		fetch: () => API.Data.Jobs.issues.getGeoValidationJobs(),
		action: {
			kind: "processGeo",
			failure: "Failed to re-process the geo data",
			choices: [
				{ label: "Re-Run Validation", perform: (jobId) => API.Data.Jobs.issues.processGeo(jobId) },
				// Processes the boundary without validating, which clears the job's Geo-Spatial Validator errors.
				{
					label: "Skip Validation",
					perform: (jobId) => API.Data.Jobs.issues.processGeo(jobId, { skipValidation: true }),
				},
			],
		},
	},
	manualReview: {
		label: "Manual Review",
		errorSource: EXIT_INTERVIEW_SOURCE,
		ageFrom: "dateSampled",
		queue: "manual-review",
		fetch: () => API.Data.Jobs.issues.getManualReviewJobs(),
		// Flags the job approved, then re-runs the exit interview, which lifts its manual-review hold.
		action: {
			kind: "approveManualReview",
			failure: "Failed to approve the manual review",
			choices: [{ label: "Approve", perform: (jobId) => API.Data.Jobs.issues.approveManualReview(jobId) }],
		},
	},
	exe: {
		label: "Exe Validation",
		errorSource: EXIT_INTERVIEW_SOURCE,
		ageFrom: "dateSampled",
		queue: "exe-mission-validation",
		fetch: () => API.Data.Jobs.issues.getExeMissionValidationJobs(),
		// The single job's re-run is the panel header's own menu (JobDetail's `reprocess`); only a batch needs it here.
		batchOnlyAction: {
			failure: "Failed to re-run file generation",
			choices: [
				{ label: "Normal", perform: (jobId) => API.Data.Jobs.issues.fixExeMissionValidationJobs([jobId], false) },
				{
					label: "Ignore Validation",
					perform: (jobId) => API.Data.Jobs.issues.fixExeMissionValidationJobs([jobId], true),
				},
			],
		},
	},
	dataSendback: {
		label: "Data Sendback",
		developerOnly: true,
		// A job is here for what was not sent, which need not have left a software error — hence the Data Sent flags.
		errorSource: null,
		showDataSent: true,
		ageFrom: "dateSampled",
		queue: "data-sendback",
		fetch: () => API.Data.Jobs.issues.getDataSendback(),
		action: {
			kind: "dataSendback",
			failure: "Failed to resend the data",
			choices: [
				// Respects the deal's sync method; the two below force one channel whatever the deal says.
				{
					label: "Re-Try Send",
					perform: (jobId) => API.Data.Jobs.issues.fixDataSendback([jobId]),
					settleMs: SENDBACK_SETTLE_MS,
				},
				{
					label: "Force Resend Email",
					perform: (jobId) => API.Data.Jobs.issues.fixDataSendback([jobId], true, false),
					settleMs: SENDBACK_SETTLE_MS,
				},
				{
					label: "Force Resend API",
					perform: (jobId) => API.Data.Jobs.issues.fixDataSendback([jobId], false, true),
					settleMs: SENDBACK_SETTLE_MS,
				},
				{
					label: "Mark CSV Sent",
					failure: "Failed to mark the CSV as sent to the lab",
					perform: (jobId) => API.Data.Jobs.update.field(jobId, "csvSentToLab", true),
				},
			],
		},
	},
	status: {
		label: "Status",
		developerOnly: true,
		errorSource: STATUS_CALCULATOR_SOURCE,
		ageFrom: "dateCreated",
		queue: "status",
		fetch: () => API.Data.Jobs.issues.getStatusIssueJobs(),
	},
	misc: {
		label: "Misc",
		// Whatever the other tabs don't cover, so there's no one source to pick.
		errorSource: null,
		ageFrom: "dateCreated",
		queue: "misc",
		fetch: () => API.Data.Jobs.issues.getMiscIssueJobs(),
	},
} satisfies Record<string, JobErrorTabConfig>;

export type JobErrorTab = keyof typeof JOB_ERROR_TABS;

const TAB_KEYS = Object.keys(JOB_ERROR_TABS) as JobErrorTab[];
const TAB_CONFIGS: Record<JobErrorTab, JobErrorTabConfig> = JOB_ERROR_TABS;

/** The tabs a user may see, in order: developer-only tabs are hidden from other admins. */
export function visibleJobErrorTabs(isDeveloper: boolean): JobErrorTab[] {
	return TAB_KEYS.filter((key) => isDeveloper || !TAB_CONFIGS[key].developerOnly);
}

/** The queues behind the tabs a user may see — what the Job Errors count badge counts, so it never counts
 *  jobs sitting on tabs the user can't open (nor asks the API for them). */
export function visibleJobErrorQueues(isDeveloper: boolean): IssueQueue[] {
	return visibleJobErrorTabs(isDeveloper).map((key) => TAB_CONFIGS[key].queue);
}

/** The action a batch over the tab's selected jobs runs, if the tab has one. */
export function batchActionOf(tab: JobErrorTab): JobErrorBatchAction | undefined {
	return TAB_CONFIGS[tab].action ?? TAB_CONFIGS[tab].batchOnlyAction;
}

/** The jobs across the given tabs' lists, each once — a job can sit on more than one tab. */
export function uniqueJobErrorJobs(lists: readonly (readonly Job[])[]): Job[] {
	const byId = new Map<string, Job>();
	for (const list of lists) for (const job of list) byId.set(job.id, job);
	return [...byId.values()];
}
